import React, { useState, useEffect } from 'react';
import { reportsApi } from '../api';
import { useNavigate } from 'react-router-dom';

const card = { background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 16 };
const kpiCard = (color) => ({
  background: '#1e293b', borderRadius: 10, padding: 16, borderLeft: `3px solid ${color}`,
  flex: '1 1 160px', minWidth: 140,
});
const sectionTitle = { color: '#f1f5f9', fontSize: 16, fontWeight: 600, marginBottom: 16 };
const label = { color: '#94a3b8', fontSize: 12, marginBottom: 4, display: 'block' };
const value = { color: '#f1f5f9', fontSize: 22, fontWeight: 700 };
const subValue = { color: '#64748b', fontSize: 12, marginTop: 2 };
const tabBtn = (active) => ({
  padding: '6px 16px', background: active ? '#818cf8' : 'transparent',
  color: active ? '#fff' : '#94a3b8', border: '1px solid',
  borderColor: active ? '#818cf8' : '#334155', borderRadius: 6,
  cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 400,
});

function KpiCard({ label: lbl, value: val, sub, color = '#6366f1' }) {
  return (
    <div style={kpiCard(color)}>
      <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>{lbl}</div>
      <div style={{ color: '#f1f5f9', fontSize: 20, fontWeight: 700 }}>{val}</div>
      {sub && <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function ReportsPage() {
  const [tab, setTab] = useState('uebersicht');
  const [uebersicht, setUebersicht] = useState(null);
  const [vertragsReport, setVertragsReport] = useState(null);
  const [kostenReport, setKostenReport] = useState(null);
  const [finanzReport, setFinanzReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jahr, setJahr] = useState(new Date().getFullYear());
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    reportsApi.uebersicht().then(r => setUebersicht(r)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === 'vertraege' && !vertragsReport) {
      reportsApi.vertraege().then(r => setVertragsReport(r));
    }
    if (tab === 'kosten') {
      reportsApi.kosten({ jahr }).then(r => setKostenReport(r));
    }
    if (tab === 'finanzen' && !finanzReport) {
      reportsApi.finanzen().then(r => setFinanzReport(r));
    }
  }, [tab, jahr]);

  const fmt = (v) => typeof v === 'number' ? v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v;

  return (
    <div style={{ padding: 32 }}>
      <h1 style={{ color: '#f1f5f9', marginBottom: 24 }}>Reports & Auswertungen</h1>

      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { key: 'uebersicht', label: '📊 Übersicht' },
          { key: 'vertraege', label: '📋 Verträge' },
          { key: 'kosten', label: '💶 Kosten' },
          { key: 'finanzen', label: '💰 Finanzen' },
        ].map(t => (
          <button key={t.key} style={tabBtn(tab === t.key)} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── ÜBERSICHT ─────────────────────────────────────── */}
      {tab === 'uebersicht' && uebersicht && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
            <KpiCard label="Aktive Verträge" value={uebersicht.vertraege_aktiv} color="#6366f1" />
            <KpiCard label="Monatskosten" value={`${fmt(uebersicht.monatskosten)} €`} color="#0ea5e9" />
            <KpiCard label="Ausgaben (Monat)" value={`${fmt(uebersicht.ausgaben_dieser_monat)} €`} color="#f59e0b" />
            <KpiCard label="Aktive Anbieter" value={uebersicht.anbieter_aktiv} color="#10b981" />
            <KpiCard label="Fristen (90 Tage)" value={uebersicht.kuendigungen_90_tage} color="#f97316" />
            <KpiCard label="Komm. überfällig" value={uebersicht.kommunikation_ueberfaellig} color="#ef4444" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            <div style={card}>
              <div style={sectionTitle}>Schnellzugriff</div>
              {[
                { label: '📋 Vertrags-Report', tab: 'vertraege' },
                { label: '💶 Kosten-Report', tab: 'kosten' },
                { label: '💰 Finanz-Report', tab: 'finanzen' },
              ].map(l => (
                <button key={l.tab}
                  onClick={() => setTab(l.tab)}
                  className="btn-secondary"
                  style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 6 }}>
                  {l.label}
                </button>
              ))}
            </div>

            <div style={card}>
              <div style={sectionTitle}>Export-Center</div>
              <div style={{ color: '#94a3b8', fontSize: 13 }}>
                Daten als CSV oder JSON exportieren
              </div>
              <button onClick={() => navigate('/export')}
                className="btn-primary"
                style={{ marginTop: 12 }}>
                Zum Export-Center
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── VERTRÄGE ──────────────────────────────────────── */}
      {tab === 'vertraege' && (
        <div>
          {!vertragsReport ? (
            <div style={{ color: '#94a3b8' }}>Lädt…</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                <KpiCard label="Gesamt" value={vertragsReport.gesamt} color="#6366f1" />
                <KpiCard label="Aktiv" value={vertragsReport.aktiv} color="#10b981" />
                <KpiCard label="Abgelaufen" value={vertragsReport.abgelaufen} color="#64748b" />
                <KpiCard label="Gekündigt" value={vertragsReport.gekuendigt} color="#f59e0b" />
                <KpiCard label="Bald ablaufend (90 T.)" value={vertragsReport.bald_ablaufend} color="#ef4444" />
              </div>

              {vertragsReport.bald_ablaufend_liste.length > 0 && (
                <div style={card}>
                  <div style={sectionTitle}>Bald ablaufende Verträge</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                        <th style={{ padding: '6px 0', textAlign: 'left' }}>Vertrag</th>
                        <th style={{ padding: '6px 0', textAlign: 'left' }}>Anbieter</th>
                        <th style={{ padding: '6px 0', textAlign: 'left' }}>Enddatum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vertragsReport.bald_ablaufend_liste.map(v => (
                        <tr key={v.id} style={{ borderBottom: '1px solid #1e293b' }}>
                          <td style={{ padding: '6px 0', color: '#f1f5f9' }}>{v.titel}</td>
                          <td style={{ padding: '6px 0', color: '#94a3b8' }}>{v.anbieter || '–'}</td>
                          <td style={{ padding: '6px 0', color: '#f87171' }}>{v.vertragsende}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={card}>
                <div style={sectionTitle}>Nach Kategorie</div>
                {vertragsReport.nach_kategorie.map(k => (
                  <div key={k.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #0f172a', fontSize: 13 }}>
                    <span style={{ color: '#f1f5f9' }}>{k.name}</span>
                    <span style={{ color: '#94a3b8' }}>{k.aktiv}/{k.gesamt} aktiv</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── KOSTEN ────────────────────────────────────────── */}
      {tab === 'kosten' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <label style={{ color: '#94a3b8', fontSize: 13 }}>Jahr:</label>
            <input
              type="number" value={jahr} min={2020} max={2030}
              onChange={e => setJahr(parseInt(e.target.value) || new Date().getFullYear())}
              style={{ padding: '6px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#f1f5f9', width: 90 }}
            />
          </div>
          {!kostenReport ? (
            <div style={{ color: '#94a3b8' }}>Lädt…</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                <KpiCard label="Jahreskosten" value={`${fmt(kostenReport.jahreskosten)} €`} color="#6366f1" />
                <KpiCard label="Ø pro Monat" value={`${fmt(kostenReport.durchschnitt_monat)} €`} color="#0ea5e9" />
                <KpiCard label="Positionen" value={kostenReport.positionen} color="#10b981" />
              </div>

              <div style={card}>
                <div style={sectionTitle}>Monatliche Kosten {kostenReport.jahr}</div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Monat</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Kosten (€)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kostenReport.monatskosten.map(m => (
                        <tr key={m.monat} style={{ borderBottom: '1px solid #0f172a' }}>
                          <td style={{ padding: '6px 8px', color: '#f1f5f9' }}>{m.monat}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: '#0ea5e9' }}>{fmt(m.kosten)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── FINANZEN ──────────────────────────────────────── */}
      {tab === 'finanzen' && (
        <div>
          {!finanzReport ? (
            <div style={{ color: '#94a3b8' }}>Lädt…</div>
          ) : (
            <>
              <div style={{ color: '#64748b', fontSize: 12, marginBottom: 12 }}>Berichtsmonat: {finanzReport.monat}</div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                <KpiCard label="Ausgaben" value={`${fmt(finanzReport.ausgaben.summe)} €`} sub={`${finanzReport.ausgaben.anzahl} Positionen`} color="#f59e0b" />
                <KpiCard label="Zahlungen" value={`${fmt(finanzReport.zahlungen.summe)} €`} sub={`${finanzReport.zahlungen.anzahl} Positionen`} color="#0ea5e9" />
              </div>

              {finanzReport.budgets.length > 0 && (
                <div style={card}>
                  <div style={sectionTitle}>Budget-Status</div>
                  {finanzReport.budgets.map(b => (
                    <div key={b.id} style={{ marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ color: '#f1f5f9', fontSize: 13 }}>{b.name}</span>
                        <span style={{ color: b.ueberzogen ? '#ef4444' : '#94a3b8', fontSize: 12 }}>
                          {fmt(b.ausgegeben)} / {fmt(b.limit)} € ({b.verbrauch_prozent}%)
                        </span>
                      </div>
                      <div style={{ height: 6, background: '#0f172a', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', borderRadius: 3, transition: 'width .3s',
                          width: `${Math.min(b.verbrauch_prozent, 100)}%`,
                          background: b.ueberzogen ? '#ef4444' : b.verbrauch_prozent > 80 ? '#f59e0b' : '#10b981',
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
